/**
 * Pure helpers + small client cache for the book coverage % badge.
 */

import type { BookCoverageResponse } from "@/lib/lumi-api";

export interface CoverageDisplay {
  /** Rounded 0–100 percentage for the pill label. */
  pct: number;
  known: number;
  total: number;
}

/**
 * Map API coverage to a display model. Returns null when the badge should be
 * hidden (null fields, non-finite values, or empty total).
 */
export function coverageDisplay(
  data: BookCoverageResponse | null | undefined,
): CoverageDisplay | null {
  if (!data) return null;
  const { known_pct, known, total } = data;
  if (
    known_pct == null ||
    known == null ||
    total == null ||
    !Number.isFinite(known_pct) ||
    !Number.isFinite(known) ||
    !Number.isFinite(total) ||
    total <= 0
  ) {
    return null;
  }
  return {
    pct: Math.round(known_pct),
    known,
    total,
  };
}

export function coverageBadgeLabel(display: CoverageDisplay): string {
  return `${display.pct}% known`;
}

// ── In-memory client cache (module-scoped, cheap) ───────────────────────────

const CACHE_TTL_MS = 60_000;

interface CacheEntry {
  data: BookCoverageResponse;
  expiresAt: number;
}

const coverageCache = new Map<string, CacheEntry>();

export function getCachedCoverage(
  bookId: string,
): BookCoverageResponse | undefined {
  const entry = coverageCache.get(bookId);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    coverageCache.delete(bookId);
    return undefined;
  }
  return entry.data;
}

export function setCachedCoverage(
  bookId: string,
  data: BookCoverageResponse,
): void {
  coverageCache.set(bookId, {
    data,
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
}

/** Test helper: wipe the module cache between specs. */
export function clearCoverageCache(): void {
  coverageCache.clear();
}
